import { useState, useEffect, useMemo, useCallback, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  DollarSign,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import AttachmentsSection from '../../components/AttachmentsSection';
import {
  getCustomerPaymentById,
  updateCustomerPayment,
} from '../../utils/customerPaymentApi';
import { getInvoiceById, getCustomerReceivableInvoices } from '../../utils/invoiceApi';
import {
  listStagedAttachments,
  processAttachmentChanges,
  syncStagedAttachmentNotes,
} from '../../utils/attachmentApi';
import { getCustomerPaymentAttachments } from '../../utils/paymentAttachmentApi';
import {
  CUSTOMER_PAYMENT_UPLOAD_SESSION_KEY,
  getOrCreateUploadSessionId,
  resetUploadSessionId,
} from '../../utils/uploadSession';
import { ApiValidationError } from '../../types/auth';
import type { CustomerPayment, CustomerPaymentMethod } from '../../types/customerPayment';
import type { Invoice } from '../../types/invoice';
import type { EditableAttachment, PendingAttachment } from '../../types/attachmentsForm';
import {
  attachmentToEditable,
  getStagedAttachmentIds,
  hasPendingUploadFailed,
  hasPendingUploadInProgress,
  stagedAttachmentToPending,
} from '../../types/attachmentsForm';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast, toastApiError } from '../../utils/toast';

const MIN_ALLOCATION = 0.01;

const PAYMENT_METHODS: { value: CustomerPaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ONLINE', label: 'Online' },
];

const canEditCustomerPayment = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const isCustomerPaymentVoided = (payment: CustomerPayment) =>
  (payment.status ?? 'POSTED') === 'VOIDED';

const formatPaymentDate = (value: string) => new Date(value).toLocaleDateString();

const parsePayment = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const syncFormFromPayment = (pmt: CustomerPayment) => ({
  paymentDate: pmt.txn_date.slice(0, 10),
  amountReceived: parseFloat(pmt.total_amount).toFixed(2),
  paymentMethod: pmt.payment_method,
  referenceNo: pmt.ref_number ?? '',
  memo: pmt.memo ?? '',
});

interface OpenInvoiceLine {
  invoice_id: number;
  invoice_number: string;
  txn_date: string;
  total: number;
  balance: number;
  selected: boolean;
  paymentAmount: string;
}

/** Minimal invoice fields used to build allocation rows. */
interface PaymentInvoiceSource {
  id: number;
  customer_id: number;
  invoice_number: string;
  txn_date: string;
  total_amount: string;
  balance: string;
  status: string;
}

const invoiceToPaymentInvoiceSource = (inv: Invoice): PaymentInvoiceSource => ({
  id: inv.id,
  customer_id: inv.customer_id,
  invoice_number: inv.invoice_number,
  txn_date: inv.txn_date,
  total_amount: inv.total_amount,
  balance: inv.balance,
  status: inv.status,
});

/** Open receivable invoices for the customer, plus any already allocated on this payment. */
const loadInvoicesForPaymentEdit = async (
  payment: CustomerPayment
): Promise<PaymentInvoiceSource[]> => {
  const byId = new Map<number, PaymentInvoiceSource>();

  const receivable = await getCustomerReceivableInvoices(payment.customer_id);
  for (const inv of receivable) {
    byId.set(inv.id, invoiceToPaymentInvoiceSource(inv));
  }

  for (const alloc of payment.allocations) {
    if (alloc.invoice) {
      byId.set(alloc.invoice_id, invoiceToPaymentInvoiceSource(alloc.invoice));
    }
  }

  const missingAllocatedIds = payment.allocations
    .map((alloc) => alloc.invoice_id)
    .filter((id) => !byId.has(id));

  if (missingAllocatedIds.length > 0) {
    const fetched = await Promise.all(
      missingAllocatedIds.map((id) => getInvoiceById(id).catch(() => null))
    );
    for (const inv of fetched) {
      if (inv && inv.customer_id === payment.customer_id) {
        byId.set(inv.id, invoiceToPaymentInvoiceSource(inv));
      }
    }
  }

  return Array.from(byId.values()).sort(
    (a, b) => new Date(a.txn_date).getTime() - new Date(b.txn_date).getTime()
  );
};

const autoApplyFifo = (lines: OpenInvoiceLine[], amount: number): OpenInvoiceLine[] => {
  let remaining = amount;
  return lines.map((line) => {
    if (remaining <= 0) {
      return { ...line, selected: false, paymentAmount: '' };
    }
    const applied = Math.min(remaining, line.balance);
    remaining -= applied;
    return {
      ...line,
      selected: applied > 0,
      paymentAmount: applied > 0 ? applied.toFixed(2) : '',
    };
  });
};

const buildInvoiceLines = (
  invoices: PaymentInvoiceSource[],
  customerId: number,
  payment: CustomerPayment
): OpenInvoiceLine[] => {
  const allocMap = new Map(
    payment.allocations.map((a) => [a.invoice_id, parseFloat(a.amount_applied)])
  );
  const allocatedIds = new Set(allocMap.keys());

  return invoices
    .filter((inv) => {
      if (inv.customer_id !== customerId) return false;
      if (allocatedIds.has(inv.id)) return true;
      return (
        (inv.status === 'OPEN' || inv.status === 'PARTIAL') && parseFloat(inv.balance) > 0
      );
    })
    .map((inv) => {
      const allocatedAmt = allocMap.get(inv.id) ?? 0;
      const effectiveBalance = parseFloat(inv.balance) + allocatedAmt;
      const selected = allocatedAmt >= MIN_ALLOCATION;
      return {
        invoice_id: inv.id,
        invoice_number: inv.invoice_number,
        txn_date: inv.txn_date.slice(0, 10),
        total: parseFloat(inv.total_amount),
        balance: effectiveBalance,
        selected,
        paymentAmount: selected ? allocatedAmt.toFixed(2) : '',
      };
    });
};

const rebuildLinesFromPayment = (
  invoices: PaymentInvoiceSource[],
  payment: CustomerPayment
): OpenInvoiceLine[] => buildInvoiceLines(invoices, payment.customer_id, payment);

export type ReceivePaymentUpdateProps = {
  embedded?: boolean;
  paymentId?: number;
  onClose?: () => void;
  onPaymentSaved?: (payment: CustomerPayment) => void;
};

const ReceivePaymentUpdate = ({
  embedded = false,
  paymentId: paymentIdProp,
  onClose,
  onPaymentSaved,
}: ReceivePaymentUpdateProps = {}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routePaymentId = Number(id);
  const paymentId =
    paymentIdProp != null && paymentIdProp > 0 ? paymentIdProp : routePaymentId;

  const goBack = () => {
    if (embedded) {
      onClose?.();
      return;
    }
    navigate(-1);
  };

  const [payment, setPayment] = useState<CustomerPayment | null>(null);
  const [lines, setLines] = useState<OpenInvoiceLine[]>([]);

  const [paymentDate, setPaymentDate] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<CustomerPaymentMethod | ''>('');
  const [referenceNo, setReferenceNo] = useState('');
  const [memo, setMemo] = useState('');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<EditableAttachment[]>([]);
  const [uploadSessionId, setUploadSessionId] = useState(() =>
    getOrCreateUploadSessionId(CUSTOMER_PAYMENT_UPLOAD_SESSION_KEY)
  );

  const loadStagedAttachments = useCallback(async () => {
    const staged = await listStagedAttachments({
      upload_session_id: uploadSessionId,
      intended_attachable_type: 'payment',
    }).catch((err) => {
      console.error('[ReceivePaymentUpdate] Failed to load staged attachments', err);
      return [];
    });
    setPendingAttachments(staged.map(stagedAttachmentToPending));
  }, [uploadSessionId]);

  const loadAttachments = useCallback(async (id: number) => {
    const attachments = await getCustomerPaymentAttachments(id).catch((err) => {
      console.error('[ReceivePaymentUpdate] Failed to load attachments', err);
      return [];
    });
    setExistingAttachments(attachments.map(attachmentToEditable));
  }, []);

  const buildAttachmentFields = () => {
    const stagedIds = getStagedAttachmentIds(pendingAttachments);
    return stagedIds.length > 0 ? { staged_attachment_ids: stagedIds } : {};
  };

  useEffect(() => {
    if (!paymentId || Number.isNaN(paymentId)) {
      setLoading(false);
      setLoadError('Invalid payment ID');
      return;
    }

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const pmt = await getCustomerPaymentById(paymentId);
        const invoices = await loadInvoicesForPaymentEdit(pmt);
        setPayment(pmt);
        setLines(buildInvoiceLines(invoices, pmt.customer_id, pmt));
        const form = syncFormFromPayment(pmt);
        setPaymentDate(form.paymentDate);
        setAmountReceived(form.amountReceived);
        setPaymentMethod(form.paymentMethod);
        setReferenceNo(form.referenceNo);
        setMemo(form.memo);
        await Promise.all([loadAttachments(paymentId), loadStagedAttachments()]);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load payment');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [paymentId]);

  const totalAmount = useMemo(() => parsePayment(amountReceived), [amountReceived]);

  const totalApplied = useMemo(
    () => lines.reduce((sum, line) => sum + parsePayment(line.paymentAmount), 0),
    [lines]
  );

  const remainingUnapplied = useMemo(
    () => Math.max(0, totalAmount - totalApplied),
    [totalAmount, totalApplied]
  );

  const totalDue = useMemo(
    () => lines.reduce((sum, line) => sum + line.balance, 0),
    [lines]
  );

  const selectedCount = useMemo(
    () => lines.filter((line) => line.selected && parsePayment(line.paymentAmount) > 0).length,
    [lines]
  );

  const allSelected =
    lines.length > 0 &&
    lines.every((line) => line.selected && parsePayment(line.paymentAmount) === line.balance);

  const isOverAllocated = totalApplied > totalAmount + MIN_ALLOCATION;

  const updateLine = (invoiceId: number, patch: Partial<OpenInvoiceLine>) => {
    setLines((prev) =>
      prev.map((line) => (line.invoice_id === invoiceId ? { ...line, ...patch } : line))
    );
  };

  const maxAmountForLine = (line: OpenInvoiceLine) => {
    const currentLineAmount = parsePayment(line.paymentAmount);
    const otherApplied = totalApplied - currentLineAmount;
    return Math.min(line.balance, Math.max(0, totalAmount - otherApplied));
  };

  const toggleLine = (invoiceId: number, checked: boolean) => {
    const line = lines.find((row) => row.invoice_id === invoiceId);
    if (!line) return;

    const maxForLine = maxAmountForLine(line);
    updateLine(invoiceId, {
      selected: checked && maxForLine >= MIN_ALLOCATION,
      paymentAmount: checked && maxForLine >= MIN_ALLOCATION ? maxForLine.toFixed(2) : '',
    });
  };

  const handlePaymentChange = (invoiceId: number, value: string) => {
    const line = lines.find((row) => row.invoice_id === invoiceId);
    if (!line) return;

    const currentLineAmount = parsePayment(line.paymentAmount);
    const otherApplied = totalApplied - currentLineAmount;
    const maxForLine = Math.min(line.balance, Math.max(0, totalAmount - otherApplied));

    const amount = parsePayment(value);
    const capped = amount > maxForLine ? maxForLine.toFixed(2) : value;

    updateLine(invoiceId, {
      paymentAmount: capped,
      selected: parsePayment(capped) >= MIN_ALLOCATION,
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setError(null);
      setLines((prev) => autoApplyFifo(prev, totalAmount));
      return;
    }

    setLines((prev) =>
      prev.map((line) => ({ ...line, selected: false, paymentAmount: '' }))
    );
  };

  const handleClearPayments = () => {
    setLines((prev) => prev.map((line) => ({ ...line, selected: false, paymentAmount: '' })));
    setError(null);
  };

  const handleAutoApply = () => {
    if (totalAmount < MIN_ALLOCATION) {
      setError('Payment total is too small to auto-apply.');
      return;
    }
    setError(null);
    setLines((prev) => autoApplyFifo(prev, totalAmount));
  };

  const handlePayAll = () => {
    setError(null);
    setLines((prev) => autoApplyFifo(prev, Math.min(totalDue, totalAmount)));
  };

  const buildAllocations = () =>
    lines
      .map((line) => ({
        invoice_id: line.invoice_id,
        amount_applied: parsePayment(line.paymentAmount),
      }))
      .filter((row) => row.amount_applied >= MIN_ALLOCATION);

  const getRowFieldError = (invoiceId: number) => {
    const allocationIndex = buildAllocations().findIndex((a) => a.invoice_id === invoiceId);
    if (allocationIndex < 0) return undefined;
    return fieldErrors[`allocations.${allocationIndex}.amount_applied`]?.[0];
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canEditCustomerPayment() || !payment || isCustomerPaymentVoided(payment)) return;

    setError(null);
    setFieldErrors({});

    if (!paymentMethod) {
      setError('Payment method is required.');
      return;
    }

    if (totalAmount < MIN_ALLOCATION) {
      setError('Payment total must be at least $0.01.');
      return;
    }

    if (paymentMethod === 'CHEQUE' && !referenceNo.trim()) {
      setError('Reference number is required for cheque payments.');
      return;
    }

    if (isOverAllocated) {
      setError(
        `Total applied (${formatCurrency(totalApplied)}) exceeds payment total (${formatCurrency(totalAmount)}).`
      );
      return;
    }

    for (const line of lines) {
      const amount = parsePayment(line.paymentAmount);
      if (amount <= 0) continue;
      if (amount > line.balance + MIN_ALLOCATION) {
        setError(`Payment for ${line.invoice_number} cannot exceed ${formatCurrency(line.balance)}.`);
        return;
      }
    }

    const allocations = buildAllocations();
    const originalAmount = parseFloat(payment.total_amount);
    const amountChanged = Math.abs(totalAmount - originalAmount) >= MIN_ALLOCATION;
    const methodChanged = paymentMethod !== payment.payment_method;

    const hasExistingAttachmentWork = existingAttachments.some(
      (row) => row.markedForDelete || row.note !== (row.attachment.note ?? '')
    );
    const hasStagedNoteWork = pendingAttachments.some(
      (row) => row.uploadStatus === 'staged' && row.note !== (row.attachment?.note ?? '')
    );

    if (hasPendingUploadInProgress(pendingAttachments)) {
      setError('Please wait for file uploads to finish before saving.');
      return;
    }
    if (hasPendingUploadFailed(pendingAttachments)) {
      setError('Remove or retry failed file uploads before saving.');
      return;
    }

    setSubmitting(true);
    try {
      if (hasStagedNoteWork) {
        const noteFailures = await syncStagedAttachmentNotes(pendingAttachments);
        if (noteFailures.length > 0) {
          setError(`Could not update attachment notes: ${noteFailures.join(' ')}`);
          return;
        }
      }

      const updated = await updateCustomerPayment(paymentId, {
        txn_date: paymentDate,
        total_amount: totalAmount,
        payment_method: paymentMethod,
        ref_number: referenceNo.trim() || null,
        memo: memo.trim() || null,
        allocations,
        ...buildAttachmentFields(),
      });

      if (hasExistingAttachmentWork && canEditCustomerPayment()) {
        const { failures } = await processAttachmentChanges({
          parent: 'payment',
          parentId: paymentId,
          pending: [],
          existing: existingAttachments,
          showIncludeOnSend: false,
        });

        if (failures.length > 0) {
          toast.error(`Payment saved. ${failures.length} attachment issue(s): ${failures.join(' ')}`);
          setPendingAttachments([]);
          setUploadSessionId(resetUploadSessionId(CUSTOMER_PAYMENT_UPLOAD_SESSION_KEY));
          await loadAttachments(paymentId);
          setPayment(updated);
          return;
        }
      }

      setPendingAttachments([]);
      setUploadSessionId(resetUploadSessionId(CUSTOMER_PAYMENT_UPLOAD_SESSION_KEY));
      await loadAttachments(paymentId);
      const refreshedInvoices = await loadInvoicesForPaymentEdit(updated);
      setPayment(updated);
      setLines(rebuildLinesFromPayment(refreshedInvoices, updated));
      const form = syncFormFromPayment(updated);
      setPaymentDate(form.paymentDate);
      setAmountReceived(form.amountReceived);
      setPaymentMethod(form.paymentMethod);
      setReferenceNo(form.referenceNo);
      setMemo(form.memo);

      const unapplied = parseFloat(updated.unapplied_amount);
      if (embedded) {
        if (amountChanged || methodChanged) {
          toast.success(
            `Payment ${updated.payment_number} updated. Adjusting journal entries were posted for amount or method changes.`
          );
        } else if (unapplied > MIN_ALLOCATION) {
          toast.success(
            `Payment saved. ${formatCurrency(unapplied)} remains as unapplied credit.`
          );
        } else {
          toast.success(`Payment ${updated.payment_number} saved and fully applied.`);
        }
        onPaymentSaved?.(updated);
        return;
      }

      if (amountChanged || methodChanged) {
        toast.success(
          `Payment ${updated.payment_number} updated. Adjusting journal entries were posted for amount or method changes.`
        );
      } else if (unapplied > MIN_ALLOCATION) {
        toast.success(
          `Payment saved. ${formatCurrency(unapplied)} remains as unapplied credit.`
        );
      } else {
        toast.success(`Payment ${updated.payment_number} saved and fully applied.`);
      }
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to update payment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!canEditCustomerPayment()) {
    if (embedded) {
      return (
        <div className="flex flex-col h-full min-h-0 bg-gray-100 p-6 items-center justify-center">
          <p className="text-gray-600 text-sm">
            Only administrators and accountants can edit customer payments.
          </p>
        </div>
      );
    }

    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center min-h-0">
          <p className="text-gray-600 text-sm">
            Only administrators and accountants can edit customer payments.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div
      className={
        embedded ? 'flex flex-col h-full min-h-0 bg-gray-100' : 'flex h-screen bg-gray-100'
      }
    >
      {!embedded && <Sidebar />}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className={`flex-1 overflow-auto ${embedded ? 'p-4' : 'p-4 md:p-6'}`}>
        <div className="w-full max-w-full">
          <div className={`flex items-center gap-3 ${embedded ? 'mb-4' : 'mb-6'}`}>
            {!embedded && (
              <button
                type="button"
                onClick={goBack}
                className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h1 className={`font-semibold text-gray-900 ${embedded ? 'text-lg' : 'text-xl md:text-2xl'}`}>
                Edit
              </h1>
              {!embedded && (
                <p className="mt-1 text-sm text-gray-500">
                  Update payment details and invoice allocations. Changing the amount or payment method
                  posts adjusting journal entries; allocation-only changes do not affect the GL.
                </p>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-20 text-sm text-gray-500 gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading payment…
            </div>
          )}

          {loadError && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{loadError}</span>
            </div>
          )}

          {!loading && !loadError && payment && isCustomerPaymentVoided(payment) && (
            <div className="bg-white rounded-lg shadow p-5 space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                This payment was voided
                {payment.voided_at ? ` on ${formatPaymentDate(payment.voided_at)}` : ''}
                {payment.voided_by?.name ? ` by ${payment.voided_by.name}` : ''}.
                {payment.void_reason ? ` Reason: ${payment.void_reason}` : ''}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Payment #</p>
                  <p className="font-semibold text-gray-900 mt-0.5">{payment.payment_number}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Customer</p>
                  <p className="font-medium text-gray-900 mt-0.5">
                    {payment.customer?.display_name ?? `Customer ID ${payment.customer_id}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Amount</p>
                  <p className="font-medium text-gray-900 mt-0.5">
                    {formatCurrency(payment.total_amount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Payment date</p>
                  <p className="font-medium text-gray-900 mt-0.5">
                    {formatPaymentDate(payment.txn_date)}
                  </p>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={goBack}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {!loading && !loadError && payment && !isCustomerPaymentVoided(payment) && (
            <form id="receive-payment-update-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-white rounded-lg shadow p-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                      Payment details
                    </h2>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm mb-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Payment #</p>
                        <p className="font-semibold text-gray-900 mt-0.5">{payment.payment_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Customer</p>
                        <p className="font-medium text-gray-900 mt-0.5">
                          {payment.customer?.display_name ?? `Customer ID ${payment.customer_id}`}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Payment date
                        </label>
                        <input
                          type="date"
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                        {fieldErrors.txn_date?.[0] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.txn_date[0]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Payment method <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={paymentMethod}
                          onChange={(e) =>
                            setPaymentMethod(e.target.value as CustomerPaymentMethod | '')
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                        >
                          <option value="">Select payment method</option>
                          {PAYMENT_METHODS.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                        {fieldErrors.payment_method?.[0] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.payment_method[0]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Reference no.
                          {paymentMethod === 'CHEQUE' && <span className="text-red-500"> *</span>}
                        </label>
                        <input
                          type="text"
                          maxLength={100}
                          value={referenceNo}
                          onChange={(e) => setReferenceNo(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          placeholder={paymentMethod === 'CHEQUE' ? 'Cheque number' : 'Bank / cheque ref'}
                        />
                        {fieldErrors.ref_number?.[0] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.ref_number[0]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Memo</label>
                        <input
                          type="text"
                          value={memo}
                          onChange={(e) => setMemo(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          placeholder="Optional"
                        />
                        {fieldErrors.memo?.[0] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.memo[0]}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <AttachmentsSection
                    title="Attachments"
                    description="Bank receipts, cheque scans, and other supporting documents. Files upload immediately and link when you save."
                    showIncludeOnSend={false}
                    parentIsDraft={false}
                    stageOnPick
                    intendedAttachableType="payment"
                    uploadSessionId={uploadSessionId}
                    pending={pendingAttachments}
                    onPendingChange={setPendingAttachments}
                    existing={existingAttachments}
                    onExistingChange={setExistingAttachments}
                    disabled={submitting}
                  />

                  <div className="flex flex-col justify-end gap-2 w-full">
                    <div className="grid grid-cols-2 gap-3 w-full">
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                        <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">
                          Amount received <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="any"
                          min="0.01"
                          value={amountReceived}
                          onChange={(e) => setAmountReceived(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className={`w-full border rounded-lg px-2.5 py-1.5 text-lg font-semibold text-gray-900 bg-white ${
                            fieldErrors.total_amount ? 'border-red-400 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {fieldErrors.total_amount?.[0] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.total_amount[0]}</p>
                        )}
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Unapplied credit</p>
                        <p
                          className={`text-lg font-semibold ${
                            isOverAllocated ? 'text-red-600' : 'text-amber-700'
                          }`}
                        >
                          {formatCurrency(remainingUnapplied)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="bg-white rounded-lg shadow">
                <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Outstanding invoices
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Select invoices and adjust amounts. Uncheck to remove an allocation.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600 whitespace-nowrap">Amount received</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(totalAmount)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleAutoApply}
                      className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                    >
                      Auto apply
                    </button>
                    <button
                      type="button"
                      onClick={handlePayAll}
                      className="px-3 py-1.5 text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg"
                    >
                      Pay all
                    </button>
                    <button
                      type="button"
                      onClick={handleClearPayments}
                      className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {lines.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-12 px-4">
                    No open invoices for this customer. Saving with no selections leaves the payment
                    as unapplied credit.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-slate-800"
                              aria-label="Select all invoices"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Invoice #
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Total
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Balance
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-40">
                            Payment
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {lines.map((line) => {
                          const paymentAmt = parsePayment(line.paymentAmount);
                          const overBalance = paymentAmt > line.balance + MIN_ALLOCATION;
                          const overTotal = totalApplied > totalAmount + MIN_ALLOCATION;
                          const rowFieldError = getRowFieldError(line.invoice_id);

                          return (
                            <tr
                              key={line.invoice_id}
                              className={line.selected ? 'bg-slate-50/60' : undefined}
                            >
                              <td className="px-4 py-2">
                                <input
                                  type="checkbox"
                                  checked={line.selected}
                                  onChange={(e) => toggleLine(line.invoice_id, e.target.checked)}
                                  className="h-4 w-4 rounded border-gray-300 text-slate-800"
                                  aria-label={`Select invoice ${line.invoice_number}`}
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                {line.txn_date}
                              </td>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                {line.invoice_number}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                                {formatCurrency(line.total)}
                              </td>
                              <td className="px-4 py-2 text-sm font-medium text-red-600 text-right whitespace-nowrap">
                                {formatCurrency(line.balance)}
                              </td>
                              <td className="px-4 py-2 text-right">
                                <div>
                                  <div className="flex items-center justify-end gap-1">
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      max={line.balance}
                                      value={line.paymentAmount}
                                      onChange={(e) =>
                                        handlePaymentChange(line.invoice_id, e.target.value)
                                      }
                                      onFocus={(e) => e.target.select()}
                                      className={`w-28 border rounded-lg px-2.5 py-1.5 text-sm text-right ${
                                        overBalance || overTotal || rowFieldError
                                          ? 'border-red-400 bg-red-50'
                                          : 'border-gray-300'
                                      }`}
                                      placeholder="0.00"
                                    />
                                    {line.balance > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => toggleLine(line.invoice_id, true)}
                                        className="text-xs text-slate-600 hover:text-slate-900 whitespace-nowrap px-1"
                                        title="Pay full balance"
                                      >
                                        Full
                                      </button>
                                    )}
                                  </div>
                                  {rowFieldError && (
                                    <p className="mt-1 text-xs text-red-600 text-right">
                                      {rowFieldError}
                                    </p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {!loading && !loadError && payment && !isCustomerPaymentVoided(payment) && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 md:px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              {lines.length > 0 && (
                <>
                  <span className="text-gray-600">
                    Invoices selected:{' '}
                    <span className="font-medium text-gray-900">{selectedCount}</span>
                  </span>
                  <span className="text-gray-600">
                    Open balance:{' '}
                    <span className="font-medium text-red-600">{formatCurrency(totalDue)}</span>
                  </span>
                </>
              )}
              {remainingUnapplied > MIN_ALLOCATION && (
                <span className="text-gray-600">
                  Unapplied credit:{' '}
                  <span className="font-medium text-amber-700">
                    {formatCurrency(remainingUnapplied)}
                  </span>
                </span>
              )}
              {isOverAllocated && (
                <span className="text-red-600 font-medium">
                  Total applied exceeds payment total
                </span>
              )}
              {lines.length > 0 && (
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-slate-600" />
                  <span className="text-gray-600">Amount to apply</span>
                  <span
                    className={`text-lg font-semibold ${
                      isOverAllocated ? 'text-red-600' : 'text-slate-900'
                    }`}
                  >
                    {formatCurrency(totalApplied)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={goBack}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="receive-payment-update-form"
                disabled={submitting || isOverAllocated || totalAmount < MIN_ALLOCATION}
                className="flex items-center px-6 py-2.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving…
                  </>
                ) : (
                  'Save payment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ReceivePaymentUpdate;
